import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import {
  ArrowPathRoundedSquareIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  EyeDropperIcon,
  EyeIcon,
  ViewColumnsIcon,
  ViewfinderCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-regular-svg-icons";
import { faFileZipper } from "@fortawesome/free-solid-svg-icons";
import {
  useCancelQuestionnaireRequest,
  useDocumentFile,
  useDownloadExportDocs,
  useDownloadExportFile,
  useExportQuestionnaireExcel,
  useGetRfiResponse,
  useGetRfiResponseFile,
  useResendQuestionnaire,
  useSupportingDocsExportVQA,
  useArchiveDocumentRequest,
  useUnarchiveDocumentRequest,
  useArchiveSponsorRfi,
  useUnarchiveSponsorRfi,
} from "../../custom-hooks/queries";
import ArchiveLibraryRequestModal from "../LibraryRequests/ArchiveLibraryRequestModal";
import { useEffect, useMemo, useRef, useState } from "react";
import Notification from "../Notification";
import moment from "moment";
import { useDecodedToken } from "../../custom-hooks/useToken";
import JSZip from "jszip";
import ModalWithTextBox from "../ModalWithTextBox";
import NotificationModal from "../NotificationModal";

/**
 * `GET Utilities/Exports/Questionnaire` must receive the same `folder` + `questionnaire_name` the
 * export worker used in Blob Storage. POST may return `file_name` with or without extension.
 */
function buildQuestionnaireExportDownloadFileName(exportFormatType, fileNameOrBase) {
  const raw = fileNameOrBase != null ? String(fileNameOrBase).trim() : "";
  const base = raw || "export";
  const lower = base.toLowerCase();
  const ext = exportFormatType === "excel" ? ".xlsx" : ".pdf";
  if (lower.endsWith(".xlsx") || lower.endsWith(".pdf")) {
    return base;
  }
  return `${base}${ext}`;
}

function buildZipExportDownloadFileName(fileNameOrBase) {
  const raw = fileNameOrBase != null ? String(fileNameOrBase).trim() : "";
  const base = raw || "export";
  if (base.toLowerCase().endsWith(".zip")) {
    return base;
  }
  return `${base}.zip`;
}

function resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded) {
  const fromRow =
    details?.organizationId ??
    details?.OrganizationId ??
    details?.organization_id ??
    details?.hypercomply_org_id ??
    details?.hc_org_id ??
    details?.sponsor_hypercomply_org_id ??
    details?.sponsor_organization_id;
  const fromToken =
    decoded?.organization_id ??
    decoded?.org_id ??
    decoded?.OrganizationId ??
    decoded?.hypercomply_org_id;
  if (isDocLibrary) {
    return fromRow ?? fromToken;
  }
  return fromRow ?? fromToken;
}

/**
 * Vendor library requests: Release / Reject — same menu shell as sponsor library (ellipsis + w-80 panel).
 */
function ProviderLibraryVendorActionsMenu({
  details,
  refetch,
  approve,
  reject,
  approveError,
  rejectError,
  rejectIsError,
}) {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const id = details?.request_id;

  const handleRelease = (e) => {
    e.stopPropagation();
    approve(id, {
      onSuccess: () => {
        setShow(true);
        setMessage("The Assessment request has been successfully approved.");
        refetch();
      },
      onError: () => {
        setMessage(approveError?.response?.data?.message);
        setShow(true);
      },
    });
  };

  const handleRejectClick = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton
          onClick={(e) => e.stopPropagation()}
          className="inline-flex justify-center w-full text-sm font-medium text-gray-700"
        >
          <EllipsisVerticalIcon className="h-5 w-5 flex-shrink-0" />
        </MenuButton>
      </div>
      <MenuItems className="z-50 cursor-pointer absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white border border-1 ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring">
        <div className="py-1">
          <MenuItem>
            {({ active }) => (
              <div
                onClick={handleRelease}
                className={`flex items-center space-x-2 ${
                  active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                } block px-4 py-2 text-sm`}
              >
                <CheckCircleIcon className="w-[16px] h-[16px]" />
                <span>Release</span>
              </div>
            )}
          </MenuItem>
          <MenuItem>
            {({ active }) => (
              <div
                onClick={handleRejectClick}
                className={`flex items-center space-x-2 ${
                  active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                } block px-4 py-2 text-sm`}
              >
                <XCircleIcon className="w-[16px] h-[16px]" />
                <span>Reject</span>
              </div>
            )}
          </MenuItem>
        </div>
      </MenuItems>
      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={approveError || rejectIsError ? "Error" : "Success"}
        error={approveError || rejectIsError}
      />
      <ModalWithTextBox
        open={open}
        setOpen={setOpen}
        onClick={reject}
        id={id}
        refetch={refetch}
        rejectError={rejectError}
      />
    </Menu>
  );
}

export const MenuDropdownAccordingToStatuses = ({
  status,
  type,
  drawerOpen,
  docId,
  isMyRFI,
  isDocLibrary,
  questionnaireId,
  sponsorRfiResponseId,
  details,
  isLegacyRfi,
  v1_doc_id,
  v1_doc_name,
  isMyDocs,
  refetch,
  hideRequestActions = false,
  isProviderLibraryVendor = false,
  approve,
  reject,
  approveError,
  rejectError,
  rejectIsError,
  isLibraryBackendAdmin = false,
  libraryRequestId = null,
  /** Admin RFI Requests grid: show Archive/Unarchive for Sponsor RFI row */
  isRfiBackendAdmin = false,
}) => {
  // Admin list DTOs may return "Rfi" / "rfi" instead of "RFI" — keep menu logic stable
  const documentTypeKey = String(type || "").toUpperCase();
  const isRfiType = documentTypeKey === "RFI";
  const isVqaType = documentTypeKey === "VQA";
  const documentTypeForApi = isRfiType || isVqaType ? documentTypeKey : type;

  // Some list endpoints (e.g. admin) return different casing for `request_status`
  const requestStatusLc = String(status ?? "")
    .trim()
    .toLowerCase();
  const isRequestStatus = (name) => requestStatusLc === name.toLowerCase();
  const isAnyRequestStatus = (...names) =>
    names.some((n) => requestStatusLc === n.toLowerCase());
  const isNotArchivedRequest = !isRequestStatus("Archived");

  const { mutate, isError, error } = useResendQuestionnaire();
  const [message, setMessage] = useState("");
  const { decoded, error: tokenError } = useDecodedToken();

  const {
    mutate: cancelMutate,
    isError: cancelError,
    error: cancelErrorMessage,
  } = useCancelQuestionnaireRequest();

  const [showNotification, setShowNotification] = useState(false);
  /** Drives toast style; resend/cancel `isError` is unrelated to archive API failures */
  const [localNotificationError, setLocalNotificationError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  /** 'library' | 'rfi' — which API to call on confirm */
  const [archiveModalTarget, setArchiveModalTarget] = useState(null);

  const { mutate: archiveMutate, isPending: archivePending } =
    useArchiveDocumentRequest();
  const { mutate: unarchiveMutate, isPending: unarchivePending } =
    useUnarchiveDocumentRequest();
  const { mutate: archiveRfiMutate, isPending: archiveRfiPending } =
    useArchiveSponsorRfi();
  const { mutate: unarchiveRfiMutate, isPending: unarchiveRfiPending } =
    useUnarchiveSponsorRfi();

  const effectiveSponsorRfiResponseId =
    sponsorRfiResponseId ??
    details?.sponsorRfiResponseId ??
    details?.SponsorRfiResponseId;

  const {
    mutate: getFile,
    data: files,
    isError: isFileError,
    error: errorMessageFile,
  } = useDocumentFile();

  const {
    mutate: downloadSupportDocs,
    data: supportDocs,
    isError: isSupportDocs,
    error: errorSupportDocs,
  } = useSupportingDocsExportVQA();

  const fileDownload = () => {
    getFile(docId);
  };

  const { mutate: exportExcel } = useExportQuestionnaireExcel();
  const {
    mutate: exportToExcel,
    data: exportData,
    isError: exportExcelError,
  } = useDownloadExportFile();

  const {
    mutate: exportToDocs,
    data: exportDocsData,
    isError: exportDocsError,
  } = useDownloadExportDocs();

  const { mutate: getRfiResponseFile, data: responseFileData } =
    useGetRfiResponseFile();
  const { mutate: getRfiResponse } = useGetRfiResponse();

  const handleExportExcel = (type) => {
    const vendorName = isDocLibrary
      ? details?.provider_name
      : details?.vendorName;
    const id = isDocLibrary
      ? details?.hc_questionnaire_id
      : details?.questionnaire_id;

    const sponsorName = isDocLibrary
      ? details?.sponsor_name ||
          details?.sponsorName ||
          decoded?.sponsor_name
      : details?.sponsorName;
    const org_id = resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded);

    const questionnaireName = isDocLibrary
      ? `Diligent-${
          isMyDocs ? details?.capabilities[0]?.rfi_name : details?.document_name
        }-${details?.requested_on}`
      : details?.name;

    const encodedQuestionnaireName = encodeURIComponent(questionnaireName);

    const data = {
      entity_id: id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: vendorName || "",
      sponsor_name: sponsorName || "",
      export_format: type,
      org_id,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        const folder = isDocLibrary
          ? details?.hc_questionnaire_id
          : details?.questionnaire_id;
        // BE returns file_name (computed base, with conditional "{template} - state, country"
        // for multi-location vendors). Doc Library items don't go through that BE flow so
        // keep the Diligent-* convention there.
        const questionnaireNames = isDocLibrary
          ? `Diligent-${
              isMyDocs
                ? details?.capabilities[0]?.rfi_name
                : details?.document_name
            }-${details?.requested_on}`
          : response?.file_name || details?.name;
        const downloadBaseName =
          response?.file_name || questionnaireNames || "export";
        const downloadPayload = {
          folder,
          questionnaire_name: buildQuestionnaireExportDownloadFileName(
            type,
            downloadBaseName,
          ),
        };
        const runDownload = (delayMs) => {
          setTimeout(() => {
            exportToExcel(downloadPayload, {
              onSuccess: () => {
                setMessage("Your download has started.");
                setShowNotification(true);
              },
              onError: (error) => {
                console.error("[MenuDropdown] exportToExcel failed:", {
                  status: error?.response?.status,
                  data: error?.response?.data,
                  message: error?.message,
                  params: downloadPayload,
                });
                if (delayMs <= 2000) {
                  runDownload(2500);
                }
              },
            });
          }, delayMs);
        };
        runDownload(2000);
      },
      onError: (error) => {
        console.error("[MenuDropdown] Export Excel failed:", {
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message,
        });
      },
    });
  };

  const handleRFILegacy = (type, doc_id, docName) => {
    const extentions = type === "excel" ? ".xlsx" : ".pdf";
    exportToExcel(
      {
        folder: v1_doc_id,
        questionnaire_name: `${v1_doc_name}${extentions}`,
      },
      {
        onSuccess: (response) => {
          setMessage("Your download has started.");
          setShowNotification(true);
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };

  const handleV1Docs = (type) => {
    const extentions = type === "excel" ? "xlsx" : "pdf";

    // Show download started message immediately when button is clicked
    setMessage("Download has started, it may take a while");
    setShowNotification(false); // Clear any existing notification
    setIsDownloading(true);

    getRfiResponse(v1_doc_id, {
      onSuccess: (response) => {
        // Add 10-second delay before calling getRfiResponseFile
        setTimeout(() => {
          let retryCount = 0;
          const maxRetries = 3;

          const attemptGetRfiResponseFile = () => {
            getRfiResponseFile(
              { rfiResponseId: v1_doc_id, extension: extentions },
              {
                onSuccess: (fileResponse) => {
                  // Success - update message to indicate completion
                  setIsDownloading(false);
                  setMessage("File download completed successfully");
                  setShowNotification(true);
                },
                onError: (error) => {
                  retryCount++;

                  if (retryCount < maxRetries) {
                    // Update message to show retry attempt
                    setMessage(
                      `Download in progress... Retry attempt ${
                        retryCount + 1
                      } of ${maxRetries}`,
                    );
                    // Retry after a short delay (1 second)
                    setTimeout(() => {
                      attemptGetRfiResponseFile();
                    }, 1000);
                  } else {
                    // All retries failed - show error message
                    setIsDownloading(false);
                    setMessage(
                      error?.response?.data?.message ||
                        "Failed to download file after multiple attempts. Please try again.",
                    );
                    setShowNotification(true);
                  }
                },
              },
            );
          };

          // Start the first attempt
          attemptGetRfiResponseFile();
        }, 10000); // 10 seconds delay
      },
      onError: (error) => {
        setIsDownloading(false);
        setMessage(
          error?.response?.data?.message ||
            "Failed to get VQQ response. Please try again.",
        );
        setShowNotification(true);
      },
    });
  };

  const handleAllDocuments = (type) => {
    const vendorName = isDocLibrary
      ? details?.provider_name
      : details?.vendorName;
    const id = isDocLibrary
      ? details?.hc_questionnaire_id
      : details?.questionnaire_id;

    const sponsorName = isDocLibrary
      ? details?.sponsor_name ||
          details?.sponsorName ||
          decoded?.sponsor_name
      : details?.sponsorName;
    const org_id = resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded);

    const questionnaireName = isDocLibrary
      ? `Diligent-${details?.document_name}-${details?.requested_on}`
      : details?.name;

    const encodedQuestionnaireName = encodeURIComponent(questionnaireName);

    // BE recognizes only "test" (responses-only file) and "alldocuments"
    // (ZIP containing the responses export plus every supporting document
    // attached to the questionnaire). Anything else falls back to the
    // responses-only path, which is why this menu was returning a ZIP with
    // just the response file and no extra documents.
    const data = {
      entity_id: id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: vendorName,
      sponsor_name: sponsorName,
      export_format: type,
      org_id,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "alldocuments",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        const folder = isDocLibrary
          ? details?.hc_questionnaire_id
          : details?.questionnaire_id;
        // BE returns file_name (computed base, with conditional location suffix for
        // multi-location vendors). Doc Library items skip the BE compute path.
        const questionnaireNames = isDocLibrary
          ? `Diligent-${details?.document_name}-${details?.requested_on}`
          : response?.file_name || details?.name;
        const zipBaseName =
          response?.file_name || questionnaireNames || "export";
        const downloadPayload = {
          folder,
          questionnaire_name: buildZipExportDownloadFileName(zipBaseName),
        };
        const runZipDownload = (delayMs) => {
          setTimeout(() => {
            exportToDocs(downloadPayload, {
              onSuccess: () => {
                setMessage("Your download has started.");
                setShowNotification(true);
              },
              onError: (error) => {
                console.error("[MenuDropdown] exportToDocs failed:", error);
                if (delayMs <= 2000) {
                  runZipDownload(2500);
                }
              },
            });
          }, delayMs);
        };
        runZipDownload(2000);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
      },
    });
  };

  const lastZipDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportDocsData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastZipDownloadedRef.current === downloadLink) return;
    lastZipDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = "document";

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            fileName = match[1].split(".")[0];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const questionnaireName = isDocLibrary
          ? `Diligent-${details?.document_name}-${details?.requested_on}`
          : details?.name;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${questionnaireName || fileName}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("ZIP download failed:", error);
      });
  }, [exportDocsData?.download_link, exportExcelError]);

  const lastExportDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastExportDownloadedRef.current === downloadLink) return;
    lastExportDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        const contentType = res.headers.get("Content-Type");

        let fileName = "document";
        let fileExtension = "xlsx"; // default

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || "xlsx";
          }
        } else if (downloadLink.includes("?")) {
          const match = downloadLink.match(/\.([a-zA-Z0-9]+)(\?|$)/);
          if (match?.[1]) {
            fileExtension = match[1];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const questionnaireName = isDocLibrary
          ? `Diligent-${details?.document_name}-${details?.requested_on}`
          : details?.name;
        const link = document.createElement("a");
        link.href = url;
        link.download = `${questionnaireName || fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("File download failed:", error);
      });
  }, [exportData?.download_link, exportExcelError]);

  const lastRfiResponseFileDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = responseFileData?.download_link;

    if (!downloadLink) return;
    if (lastRfiResponseFileDownloadedRef.current === downloadLink) return;
    lastRfiResponseFileDownloadedRef.current = downloadLink;

    // Extract filename from URL before the query parameters
    const extractFileNameFromUrl = (url) => {
      try {
        // Remove query parameters and get the pathname
        const urlWithoutQuery = url.split("?")[0];
        // Extract the last part of the path (filename)
        const pathParts = urlWithoutQuery.split("/");
        const fileName = pathParts[pathParts.length - 1];

        if (fileName && fileName.includes(".")) {
          const parts = fileName.split(".");
          const name = parts.slice(0, -1).join(".");
          const extension = parts[parts.length - 1];
          return { fileName: name, fileExtension: extension };
        }
        return {
          fileName: fileName || "rfi_response_file",
          fileExtension: "pdf",
        };
      } catch (error) {
        console.error("Error extracting filename from URL:", error);
        return { fileName: "rfi_response_file", fileExtension: "pdf" };
      }
    };

    const { fileName: extractedFileName, fileExtension: extractedExtension } =
      extractFileNameFromUrl(downloadLink);

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = extractedFileName;
        let fileExtension = extractedExtension;

        // If Content-Disposition header has filename, use that instead
        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || extractedExtension;
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show completion message
        setIsDownloading(false);
        setMessage("File download completed successfully");
        setShowNotification(true);
      })
      .catch((error) => {
        console.error("RFI Response file download failed:", error);
        setIsDownloading(false);
        setMessage("Failed to download file. Please try again.");
        setShowNotification(true);
      });
  }, [responseFileData?.download_link]);

  // Function to extract filename from URL
  const extractFilenameFromUrl = (url) => {
    try {
      // Remove query parameters and get the pathname
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Extract filename from pathname
      const filename = pathname.split("/").pop();

      // Decode URL-encoded characters
      return decodeURIComponent(filename);
    } catch (error) {
      console.error("Error extracting filename from URL:", error);
      return "file.pdf"; // Fallback filename
    }
  };

  useEffect(() => {
    if (isFileError) {
      return;
    }

    if (files?.download_link) {
      // Extract filename from the URL
      const filename = extractFilenameFromUrl(files.download_link);

      // Fetch the file and trigger download
      fetch(files.download_link)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.blob(); // Convert the response to a Blob
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob); // Create an Object URL
          const a = document.createElement("a");
          a.style.display = "none"; // Hide the anchor element
          a.href = url;
          a.download = filename; // Use extracted filename for download
          document.body.appendChild(a);
          a.click(); // Programmatically trigger the click
          document.body.removeChild(a); // Clean up
          URL.revokeObjectURL(url); // Release the Object URL
        })
        .catch((error) => {
          console.error("Failed to download file:", error);
        });
    }
  }, [files, isFileError]);

  const navigate = useNavigate();

  const handleSupportingDocsDownloadVQA = () => {
    downloadSupportDocs({
      entityId: sponsorRfiResponseId,
      entityType: documentTypeForApi,
    });
  };

  useEffect(() => {
    if (errorSupportDocs) {
      setShowNotification(true);
      return;
    }

    if (supportDocs?.items?.length) {
      const zip = new JSZip();
      const promises = supportDocs?.items.map((file, index) =>
        fetch(file.document_url)
          .then((res) => res.blob())
          .then((blob) => {
            const documentName = file.document_name;
            zip.file(documentName, blob);
          }),
      );

      Promise.all(promises).then(() => {
        zip.generateAsync({ type: "blob" }).then((zipBlob) => {
          const zipLink = document.createElement("a");
          zipLink.href = URL.createObjectURL(zipBlob);
          zipLink.setAttribute(
            "download",
            `DiligentPharmaAssessment-${moment().format("YYYY-MM-DD")}.zip`,
          ); // ZIP filename
          document.body.appendChild(zipLink);
          zipLink.click();
          document.body.removeChild(zipLink);
        });
      });
    }
  }, [supportDocs, errorSupportDocs]);

  const menuHasActions = useMemo(() => {
    if (isProviderLibraryVendor) return true;
    const lc = String(status ?? "").trim().toLowerCase();
    const req = (n) => lc === n.toLowerCase();
    const any = (...names) => names.some((n) => lc === n.toLowerCase());
    const notArchived = !req("archived");

    const releasedOrCompleteRfiWithQuestionnaire =
      any("complete", "released") &&
      isRfiType &&
      questionnaireId !== null;

    return (
      (isRfiType && isMyRFI && !isLegacyRfi) ||
      (any("complete", "released") && isRfiType && isMyRFI && !isLegacyRfi) ||
      (releasedOrCompleteRfiWithQuestionnaire && isDocLibrary) ||
      releasedOrCompleteRfiWithQuestionnaire ||
      (any("published", "cancelled") && isRfiType && isDocLibrary) ||
      (req("pending") && isRfiType && isDocLibrary) ||
      (req("sent") && isRfiType) ||
      (any("pending", "sent") && isRfiType && !hideRequestActions) ||
      (req("in progress") && isRfiType && isDocLibrary) ||
      (req("released") && isVqaType && isDocLibrary) ||
      (req("published") && isVqaType && isDocLibrary && !hideRequestActions) ||
      (req("upcoming") && isVqaType && isDocLibrary && !hideRequestActions) ||
      (isLegacyRfi && isRfiType && req("released")) ||
      (isLegacyRfi && isRfiType && req("complete") && isMyRFI) ||
      (isLibraryBackendAdmin &&
        isDocLibrary &&
        isMyDocs &&
        libraryRequestId &&
        req("archived")) ||
      (isLibraryBackendAdmin &&
        isDocLibrary &&
        isMyDocs &&
        libraryRequestId &&
        notArchived) ||
      (isRfiBackendAdmin &&
        isRfiType &&
        !isDocLibrary &&
        effectiveSponsorRfiResponseId &&
        !isLegacyRfi &&
        req("archived")) ||
      (isRfiBackendAdmin &&
        isRfiType &&
        !isDocLibrary &&
        effectiveSponsorRfiResponseId &&
        !isLegacyRfi &&
        notArchived)
    );
  }, [
    isProviderLibraryVendor,
    status,
    isRfiType,
    isVqaType,
    isMyRFI,
    isLegacyRfi,
    isDocLibrary,
    questionnaireId,
    hideRequestActions,
    isLibraryBackendAdmin,
    isMyDocs,
    libraryRequestId,
    isRfiBackendAdmin,
    effectiveSponsorRfiResponseId,
  ]);

  if (isProviderLibraryVendor) {
    return (
      <ProviderLibraryVendorActionsMenu
        details={details}
        refetch={refetch}
        approve={approve}
        reject={reject}
        approveError={approveError}
        rejectError={rejectError}
        rejectIsError={rejectIsError}
      />
    );
  }

  if (!menuHasActions) {
    return null;
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton
          onClick={(e) => e.stopPropagation()}
          className="inline-flex justify-center w-full text-sm font-medium text-gray-700"
        >
          <EllipsisVerticalIcon className="h-5 w-5 flex-shrink-0" />
        </MenuButton>
      </div>
      {/* Dropdown menu */}
      <MenuItems className="z-50 cursor-pointer absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white border border-1 ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring">
        <div className="py-1">
          {isRfiType && isMyRFI && !isLegacyRfi && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/RFI/${questionnaireId}/Details`);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  {" "}
                  <EyeIcon className="w-[16px] h-[16px]" />
                  <span>View Details</span>{" "}
                </div>
              )}
            </MenuItem>
          )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            isMyRFI &&
            !isLegacyRfi && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/RFI/${questionnaireId}/Details?page=responses`,
                      );
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    {" "}
                    <EyeIcon className="w-[16px] h-[16px]" />
                    <span>View Responses</span>{" "}
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            isDocLibrary &&
            questionnaireId !== null && (
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/RFI/${questionnaireId}/Details`);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ViewfinderCircleIcon className="w-[16px] h-[16px]" />
                    <span>View Details</span>
                  </div>
                )}
              </Menu.Item>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            isDocLibrary &&
            questionnaireId !== null && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/RFI/${questionnaireId}/Details?page=responses`,
                      );
                    }}
                    className={` flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ViewColumnsIcon className="w-[16px] h-[16px]" />{" "}
                    <span>View Responses</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            questionnaireId !== null && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportExcel("excel");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileExcel}
                      className="w-[16px] h-[16px] text-green-700"
                    />
                    <span>Excel - Responses only</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            questionnaireId !== null && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportExcel("pdf");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="w-[16px] h-[16px] text-red-700"
                    />
                    <span>PDF - Responses only</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            questionnaireId !== null && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAllDocuments("excel");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileZipper}
                      className="w-[16px] h-[16px] text-rose-700"
                    />
                    <span>Responses + Documents in Excel</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            questionnaireId !== null && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAllDocuments("pdf");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileZipper}
                      className="w-[16px] h-[16px] text-rose-700"
                    />
                    <span>Responses + Documents in PDF</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Published", "Cancelled") &&
            isRfiType &&
            isDocLibrary && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request Access</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isRequestStatus("Pending") && isRfiType && isDocLibrary && (
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/RFI/${questionnaireId}/Details`);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <ViewfinderCircleIcon className="w-[16px] h-[16px]" />
                  <span>View Details</span>
                </div>
              )}
            </Menu.Item>
          )}
          {isRequestStatus("Sent") && isRfiType && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    mutate(
                      { id: questionnaireId },
                      {
                        onSuccess: () => {
                          setShowNotification(true);
                          setMessage("Request resend successfully");
                          refetch();
                        },
                        onError: (error) => {
                          setShowNotification(true);
                          setMessage(
                            error?.response?.data?.message ||
                              "Error resending request",
                          );
                        },
                      },
                    );
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <EnvelopeIcon className="w-[16px] h-[16px]" />
                  <span>Resend Invite</span>
                </div>
              )}
            </MenuItem>
          )}

          {isAnyRequestStatus("Pending", "Sent") &&
            isRfiType &&
            !hideRequestActions && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelMutate(
                        { id: questionnaireId },
                        {
                          onSuccess: () => {
                            setShowNotification(true);
                            setMessage("Request cancelled successfully");
                            refetch();
                          },
                          onError: (error) => {
                            setShowNotification(true);
                            setMessage(
                              error?.response?.data?.message ||
                                "Error cancelling request",
                            );
                          },
                        },
                      );
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    {" "}
                    <XCircleIcon className="w-[16px] h-[16px]" />
                    <span>Cancel Request</span>{" "}
                  </div>
                )}
              </MenuItem>
            )}
          {isRequestStatus("In Progress") && isRfiType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  View Questionniare{" "}
                </div>
              )}
            </MenuItem>
          )}

          {isRequestStatus("Released") && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    fileDownload();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFile}
                    className="w-[16px] h-[16px] text-gray-700"
                  />
                  <span>Download Report</span>
                </div>
              )}
            </MenuItem>
          )}
          {/* {isRequestStatus("Released") && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSupportingDocsDownloadVQA();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileZipper}
                    className="w-[16px] h-[16px] text-rose-700"
                  />
                  <span>Download Report + Supporting Documents</span>
                </div>
              )}
            </MenuItem>
          )} */}
          {isRequestStatus("Published") &&
            isVqaType &&
            isDocLibrary &&
            !hideRequestActions && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request access</span>
                  </div>
                )}
              </MenuItem>
            )}
          {/* {status === "Pending" && isVqaType && isDocLibrary && !hideRequestActions && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <XCircleIcon className="w-[16px] h-[16px]" />
                  <span>Cancel Request</span>{" "}
                </div>
              )}
            </MenuItem>
          )} */}
          {isRequestStatus("Upcoming") &&
            isVqaType &&
            isDocLibrary &&
            !hideRequestActions && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request access</span>
                  </div>
                )}
              </MenuItem>
            )}

          {isLegacyRfi && isRfiType && isRequestStatus("Released") && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("excel");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="w-[16px] h-[16px] text-green-700"
                  />
                  <span>Excel - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRfiType && isRequestStatus("Released") && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("pdf");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className="w-[16px] h-[16px] text-red-700"
                  />
                  <span>PDF - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRequestStatus("Complete") && isMyRFI && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("excel");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="w-[16px] h-[16px] text-green-700"
                  />
                  <span>Excel - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRequestStatus("Complete") && isMyRFI && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("pdf");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className="w-[16px] h-[16px] text-red-700"
                  />
                  <span>PDF - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {/* {status === "Upcoming" && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/Assessment/Calendar");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  {" "}
                  <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                  <span>Join Assessment</span>{" "}
                </div>
              )}
            </MenuItem>
          )}  */}
          {isLibraryBackendAdmin &&
            isDocLibrary &&
            isMyDocs &&
            libraryRequestId &&
            isRequestStatus("Archived") && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      unarchiveMutate(libraryRequestId, {
                        onSuccess: () => {
                          setLocalNotificationError(false);
                          setShowNotification(true);
                          setMessage("Request unarchived successfully.");
                          refetch?.();
                        },
                        onError: (err) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            err?.response?.data?.message ||
                              "Could not unarchive this request."
                          );
                        },
                      });
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm ${unarchivePending ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <ArrowPathIcon className="w-[16px] h-[16px]" />
                    <span>Unarchive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isLibraryBackendAdmin &&
            isDocLibrary &&
            isMyDocs &&
            libraryRequestId &&
            isNotArchivedRequest && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchiveModalTarget("library");
                      setArchiveModalOpen(true);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArchiveBoxIcon className="w-[16px] h-[16px]" />
                    <span>Archive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isRfiBackendAdmin &&
            isRfiType &&
            !isDocLibrary &&
            effectiveSponsorRfiResponseId &&
            !isLegacyRfi &&
            isRequestStatus("Archived") && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      unarchiveRfiMutate(effectiveSponsorRfiResponseId, {
                        onSuccess: () => {
                          setLocalNotificationError(false);
                          setShowNotification(true);
                          setMessage("Request unarchived successfully.");
                          refetch?.();
                        },
                        onError: (err) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            err?.response?.data?.message ||
                              "Could not unarchive this request."
                          );
                        },
                      });
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm ${unarchiveRfiPending ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <ArrowPathIcon className="w-[16px] h-[16px]" />
                    <span>Unarchive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isRfiBackendAdmin &&
            isRfiType &&
            !isDocLibrary &&
            effectiveSponsorRfiResponseId &&
            !isLegacyRfi &&
            isNotArchivedRequest && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchiveModalTarget("rfi");
                      setArchiveModalOpen(true);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArchiveBoxIcon className="w-[16px] h-[16px]" />
                    <span>Archive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
        </div>
      </MenuItems>

      <ArchiveLibraryRequestModal
        open={archiveModalOpen}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveModalTarget(null);
        }}
        isSubmitting={archivePending || archiveRfiPending}
        requestLabel={
          archiveModalTarget === "rfi"
            ? details?.vendorName || details?.provider_name || ""
            : details?.provider_name || ""
        }
        onConfirm={(archiveComment) => {
          if (archiveModalTarget === "rfi" && effectiveSponsorRfiResponseId) {
            archiveRfiMutate(
              { sponsorRfiResponseId: effectiveSponsorRfiResponseId, archiveComment },
              {
                onSuccess: () => {
                  setArchiveModalOpen(false);
                  setArchiveModalTarget(null);
                  setShowNotification(true);
                  setMessage("Request archived successfully.");
                  refetch?.();
                },
                onError: (err) => {
                  setArchiveModalOpen(false);
                  setArchiveModalTarget(null);
                  setShowNotification(true);
                  setMessage(
                    err?.response?.data?.message ||
                      "Could not archive this request."
                  );
                },
              }
            );
            return;
          }
          archiveMutate(
            { requestId: libraryRequestId, archiveComment },
            {
              onSuccess: () => {
                setArchiveModalOpen(false);
                setArchiveModalTarget(null);
                setShowNotification(true);
                setMessage("Request archived successfully.");
                refetch?.();
              },
              onError: (err) => {
                setArchiveModalOpen(false);
                setArchiveModalTarget(null);
                setShowNotification(true);
                setMessage(
                  err?.response?.data?.message ||
                    "Could not archive this request."
                );
              },
            }
          );
        }}
      />

      <Notification
        setShow={setShowNotification}
        show={showNotification && !isDownloading}
        status={isError || cancelError || localNotificationError ? "error" : "success"}
        errorMessage={message}
      />

      {/* Custom notification for download process that doesn't auto-hide */}
      {isDownloading && (
        <div
          aria-live="assertive"
          className="pointer-events-none mt-12 fixed inset-0 flex items-start px-4 py-6 sm:items-start sm:p-6 z-[9999]"
        >
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-blue-600" style={{ marginTop: "-3px" }}>
                      Downloading...
                    </p>
                    <p className="mt-1 text-base text-gray-500 break-words">
                      {message}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-shrink-0">
                    <button
                      type="button"
                      className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        setIsDownloading(false);
                        setShowNotification(false);
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Menu>
  );
};
